import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  DollarSign, 
  TrendingUp,
  Calendar,
  ShoppingCart,
  Download,
  FileText
} from 'lucide-react';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const TransactionHistory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [earnings, setEarnings] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [transactionsRes, earningsRes] = await Promise.all([
        axios.get(`${API}/instructor/transactions`, config),
        axios.get(`${API}/instructor/earnings`, config)
      ]);

      setTransactions(transactionsRes.data);
      setEarnings(earningsRes.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        toast.error('Bu sayfaya erişim için onaylı eğitmen olmanız gerekiyor');
        navigate('/application-status');
      } else if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Veri yüklenirken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { label: 'Tamamlandı', className: 'bg-green-100 text-green-800' },
      pending: { label: 'Beklemede', className: 'bg-yellow-100 text-yellow-800' },
      failed: { label: 'Başarısız', className: 'bg-red-100 text-red-800' },
      refunded: { label: 'İade Edildi', className: 'bg-gray-100 text-gray-800' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 wellness-gradient">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/instructor/dashboard')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Dashboard'a Dön
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">İşlem Geçmişi</h1>
          <p className="text-gray-600">Tüm satışlarınız ve gelirleriniz</p>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-green-800">Toplam Gelir</CardTitle>
              <DollarSign className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-700">
                {formatCurrency(earnings?.total_earnings)}
              </div>
              <p className="text-xs text-green-600 mt-1">Net kazancınız</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-blue-800">Toplam Satış</CardTitle>
              <ShoppingCart className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-700">
                {earnings?.total_sales || 0}
              </div>
              <p className="text-xs text-blue-600 mt-1">Tamamlanan sipariş</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-purple-800">Platform Payı</CardTitle>
              <TrendingUp className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-700">
                %{earnings?.platform_fee_percentage || 20}
              </div>
              <p className="text-xs text-purple-600 mt-1">Komisyon oranı</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-orange-800">Bu Ay</CardTitle>
              <Calendar className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-700">
                {formatCurrency(
                  earnings?.monthly_breakdown?.[new Date().toISOString().slice(0, 7)] || 0
                )}
              </div>
              <p className="text-xs text-orange-600 mt-1">Aylık gelir</p>
            </CardContent>
          </Card>
        </div>

        {/* Transactions Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>İşlem Listesi</CardTitle>
                <CardDescription>Son 100 işlem görüntüleniyor</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">Henüz işlem yok</h3>
                <p className="text-gray-500">
                  Kurslarınız satıldığında işlemler burada görünecek
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Kurs</TableHead>
                      <TableHead>Müşteri</TableHead>
                      <TableHead className="text-right">Tutar</TableHead>
                      <TableHead className="text-right">Komisyon</TableHead>
                      <TableHead className="text-right">Net Gelir</TableHead>
                      <TableHead>Durum</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((tx) => {
                      const platformFee = (tx.total_amount || 0) * 0.2;
                      const instructorShare = tx.instructor_share || (tx.total_amount - platformFee);
                      
                      return (
                        <TableRow key={tx.id}>
                          <TableCell className="text-sm text-gray-600">
                            {formatDate(tx.created_at)}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{tx.lesson?.title || tx.lesson_id}</div>
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {tx.user?.full_name || tx.user?.email || '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(tx.total_amount)}
                          </TableCell>
                          <TableCell className="text-right text-sm text-red-600">
                            -{formatCurrency(platformFee)}
                          </TableCell>
                          <TableCell className="text-right font-bold text-green-600">
                            {formatCurrency(instructorShare)}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(tx.status)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TransactionHistory;
